import { useEffect } from "react";

import { useT } from "@/i18n";

import { useEnvironmentTier } from "./use-environment-tier";

/**
 * Set `document.title` for the current route.
 *
 * A single-page app never reloads, so without this the title stays whatever
 * `index.html` shipped with — which fails WCAG 2.4.2 (Page Titled) and, more
 * practically, leaves a user with six tabs open unable to tell which one is
 * their IT portal. Screen readers announce the title on navigation, so it is
 * also the clearest signal that the route actually changed.
 *
 * Pass the page's already-translated heading; the app name is appended here so
 * every page composes the same way. Off LIVE the tier leads, so a UAT tab is
 * told apart from the real portal in the tab strip: "[UAT] My tickets · …".
 *
 *   useDocumentTitle(t("tickets.title"));   // "My tickets · Norican IT HelpDesk"
 *
 * @param title The page's own title, already translated. Pass `null` while it
 *   is still loading to show the app name alone rather than a flash of "null".
 */
export function useDocumentTitle(title: string | null | undefined): void {
  const { t } = useT();
  const appName = t("app.name");
  const tier = useEnvironmentTier();

  useEffect(() => {
    document.title = composeTitle(title, appName, tier);
  }, [title, appName, tier]);
}

/**
 * `useDocumentTitle` for pages a signed-out visitor sees (`/login`, the 404).
 *
 * The same composition without the tier prefix: the tier comes from the
 * capabilities endpoint, which needs a session, so asking for it here would
 * only put a 401 in the console of everyone who has not signed in yet.
 */
export function usePublicDocumentTitle(title: string | null | undefined): void {
  const { t } = useT();
  const appName = t("app.name");

  useEffect(() => {
    document.title = composeTitle(title, appName, null);
  }, [title, appName]);
}

function composeTitle(
  title: string | null | undefined,
  appName: string,
  tier: string | null,
): string {
  const resolved = title === null || title === undefined || title === "" ? "" : title;
  const prefix = tier ? `[${tier}] ` : "";
  return prefix + (resolved === "" ? appName : `${resolved} · ${appName}`);
}
